import {
  PDF_ANSWER_BOX_DEFAULT_FONT_SIZE,
  PDF_CUSTOM_CHECKBOX_DEFAULT_SIZE,
} from "@/constants/UiConstants"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { pdfWorkerUrl } from "@/lib/pdfjsWorker"
import {
  IconMinus,
  IconPlus,
  IconSquareLetterT,
  IconSquareLetterX,
  IconTrash,
  IconX,
} from "@tabler/icons-react"
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"
import { PdfHeaderActionButton } from "./PdfHeaderActionButton"
import { PdfHighlightBox } from "./PdfHighlightBox"
import {
  PdfSignatureDropdown,
  type SelectedSignature,
} from "./PdfSignatureDropdown"

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

export type LayoutType = {
  width: number
  height: number
  x: number
  y: number
}

export type HighlightBoxType = {
  width: number
  height: number
  x: number
  y: number
  id: string
  fontSize?: number
  pageNumber: number
  title: string
  className?: string
  /** "signature" renders `imageUrl` as an image and locks the aspect ratio on
   *  resize. Both fields are optional and inert by default — every other box
   *  (and every other viewer consumer) is unaffected. */
  kind?: "answer" | "signature"
  imageUrl?: string
}

interface PdfViewerProps {
  fileUrl: string | undefined
  showControls?: boolean
  className?: string
  onClose?: () => void
  initialPage?: number
  onErrorComponent?: React.ReactElement
  onLoadingComponent?: React.ReactElement
  highlightBoxesArray: Array<HighlightBoxType>
  onHighlightResize?: (id: string, layout: LayoutType) => void
  onHighlightDragEnd?: (id: string, x: number, y: number) => void
  onBoxClick?: (id: string) => void
  handleIncreaseFontSize?: () => void
  handleDecreaseFontSize?: () => void
  selectedBoxId?: string
  onAddAnswerBox?: (placement: {
    page: number
    x: number
    y: number
    width: number
    height: number
    font_size: number
  }) => void
  isAddAnswerBoxPending?: boolean

  onAddCheckboxBox?: (placement: {
    page: number
    x: number
    y: number
    width: number
    height: number
    font_size: number
  }) => void
  isAddCheckboxBoxPending?: boolean

  onAddSignature?: (placement: {
    page: number
    x: number
    y: number
    width: number
    height: number
    signatureId: string
  }) => void
  isAddSignaturePending?: boolean
  onRemoveSignature?: (boxId: string) => void
  /** Forward to each PdfHighlightBox: when true, applies the overflow-hidden inner wrapper and
   *  lineHeight so text fits the cell the same way the server-rendered PDF does (USE_EXTEND only). */
  fitHighlightBoxText?: boolean
  /** USE_EXTEND flag. When true, overlay boxes are scaled by each page's own dimensions
   *  (correct for PDFs that mix page sizes). When false, legacy single-page-size behaviour. */
  useExtend?: boolean
}

const useContainerWidth = (containerRef: RefObject<HTMLDivElement | null>) => {
  const [cw, setCw] = useState<number | undefined>(undefined)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current && containerRef.current.offsetWidth !== cw) {
        setCw(containerRef.current.offsetWidth)
      }
    }

    updateWidth()
  }, [containerRef, cw])

  return useMemo(() => cw, [cw])
}

export const PdfIntelligenceViewer: React.FC<PdfViewerProps> = ({
  fileUrl,
  showControls = true,
  onClose,
  initialPage,
  className,
  onErrorComponent,
  onLoadingComponent,
  highlightBoxesArray,
  onHighlightResize,
  onHighlightDragEnd,
  handleIncreaseFontSize,
  handleDecreaseFontSize,
  onBoxClick,
  selectedBoxId,
  onAddAnswerBox,
  isAddAnswerBoxPending,
  onAddCheckboxBox,
  isAddCheckboxBoxPending,
  onAddSignature,
  isAddSignaturePending,
  onRemoveSignature,
  fitHighlightBoxText = false,
  useExtend = false,
}) => {
  const { translate } = useTranslation()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(1.0)
  const [pdfPageWidthInPoints, setPdfPageWidthInPoints] = useState<number>(595) // Default A4 width in points
  const [pdfPageHeightInPoints, setPdfPageHeightInPoints] =
    useState<number>(842) // Default A4 height in points
  const containerRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)
  const containerWidth = useContainerWidth(containerRef)
  const [activePageNumber, setActivePageNumber] = useState<number>(1)
  const rafScrollRef = useRef<number | null>(null)

  const pageWidth = useMemo(() => {
    if (!containerWidth) return undefined
    return Math.max(containerWidth - 40, 100)
  }, [containerWidth])

  // Each page's intrinsic point dimensions, captured as it renders. A merged PDF can mix
  // page sizes (e.g. Letter + A4), so an overlay box must be scaled by ITS OWN page's
  // dimensions — a single document-wide size skews every differently-sized page toward a
  // corner. Falls back to the page-1 / A4-default size until a page has rendered.
  const [pageDimsByNumber, setPageDimsByNumber] = useState<
    Map<number, { width: number; height: number }>
  >(new Map())

  const getPageDims = useCallback(
    (pageNumber: number) => {
      // USE_EXTEND only: use this page's own measured dimensions. When the flag is off (or the
      // page hasn't been measured yet), fall back to the document-wide page-1/default size —
      // identical to the pre-existing behaviour, so legacy/prod rendering is unchanged.
      if (useExtend) {
        const dims = pageDimsByNumber.get(pageNumber)
        if (dims) return dims
      }
      return { width: pdfPageWidthInPoints, height: pdfPageHeightInPoints }
    },
    [useExtend, pageDimsByNumber, pdfPageWidthInPoints, pdfPageHeightInPoints],
  )

  const pointsToPixelsForPage = useCallback(
    (pageNumber: number) => {
      if (!pageWidth) return 1
      return pageWidth / getPageDims(pageNumber).width
    },
    [pageWidth, getPageDims],
  )

  const scrollToPage = useCallback((pageNumber: number) => {
    requestAnimationFrame(() => {
      const pageElement = document.querySelector(
        `[data-page-number="${pageNumber}"]`,
      )
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth", block: "start" })
        hasScrolledRef.current = true
      }
    })
  }, [])

  useEffect(() => {
    if (
      containerWidth &&
      initialPage !== undefined &&
      !hasScrolledRef.current
    ) {
      scrollToPage(initialPage)
    }
  }, [initialPage, numPages, containerWidth, scrollToPage])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const handleZoomIn = () => {
    if (!containerRef.current) return
    const prevScale = scale
    const newScale = Math.min(prevScale + 0.25, 3.0)
    setScale(newScale)
  }

  const handleZoomOut = () => {
    if (!containerRef.current) return
    const prevScale = scale
    const newScale = Math.max(prevScale - 0.25, 0.5)
    setScale(newScale)
  }

  const handleResize = (id: string, layout: LayoutType, pageNumber: number) => {
    // Convert from scaled pixels back to PDF points, using THIS page's dimensions.
    const pageW = getPageDims(pageNumber).width
    const pixelsToPoints = pageW / (pageWidth || pageW)
    const newLayout = {
      width: (layout.width / scale) * pixelsToPoints,
      height: (layout.height / scale) * pixelsToPoints,
      x: (layout.x / scale) * pixelsToPoints,
      y: (layout.y / scale) * pixelsToPoints,
    }

    onHighlightResize?.(id, newLayout)
  }

  const handleDragStop = (
    id: string,
    x: number,
    y: number,
    pageNumber: number,
  ) => {
    // Convert from scaled pixels back to PDF points, using THIS page's dimensions.
    const pageW = getPageDims(pageNumber).width
    const pixelsToPoints = pageW / (pageWidth || pageW)
    onHighlightDragEnd?.(
      id,
      (x / scale) * pixelsToPoints,
      (y / scale) * pixelsToPoints,
    )
  }

  const updateActivePageFromScroll = useCallback(() => {
    if (!containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const midY = containerRect.top + containerRect.height / 2

    const wrappers = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(
        "[data-pdf-page-wrapper]",
      ),
    )

    if (!wrappers.length) return

    let bestPage = activePageNumber
    let bestDist = Number.POSITIVE_INFINITY

    for (const el of wrappers) {
      const rect = el.getBoundingClientRect()
      if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
        continue
      }
      const pageMid = rect.top + rect.height / 2
      const dist = Math.abs(pageMid - midY)
      if (dist < bestDist) {
        bestDist = dist
        const page = Number(el.dataset.pdfPageWrapper)
        if (!Number.isNaN(page)) bestPage = page
      }
    }

    setActivePageNumber(bestPage)
  }, [activePageNumber])

  const handleScroll = useCallback(() => {
    if (rafScrollRef.current) {
      cancelAnimationFrame(rafScrollRef.current)
    }
    rafScrollRef.current = requestAnimationFrame(() => {
      updateActivePageFromScroll()
    })
  }, [updateActivePageFromScroll])

  useEffect(() => {
    updateActivePageFromScroll()
  }, [containerWidth, numPages, updateActivePageFromScroll])

  const handleAddAnswerBoxClick = useCallback(() => {
    if (!onAddAnswerBox || !containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const wrapper = containerRef.current.querySelector<HTMLElement>(
      `[data-pdf-page-wrapper="${activePageNumber}"]`,
    )
    if (!wrapper) return

    const wrapperRect = wrapper.getBoundingClientRect()
    const centerX = containerRect.left + containerRect.width / 2
    const centerY = containerRect.top + containerRect.height / 2

    const localX = centerX - wrapperRect.left
    const localY = centerY - wrapperRect.top

    const activePageDims = getPageDims(activePageNumber)
    const pixelsToPoints =
      activePageDims.width / (pageWidth || activePageDims.width)

    const defaultWidth = 200
    const defaultHeight = 24
    const defaultFontSize = PDF_ANSWER_BOX_DEFAULT_FONT_SIZE

    const xPoints = (localX / scale) * pixelsToPoints - defaultWidth / 2
    const yPoints = (localY / scale) * pixelsToPoints - defaultHeight / 2

    const clampedX = Math.max(
      0,
      Math.min(xPoints, activePageDims.width - defaultWidth),
    )
    const clampedY = Math.max(
      0,
      Math.min(yPoints, activePageDims.height - defaultHeight),
    )

    onAddAnswerBox({
      page: activePageNumber,
      x: clampedX,
      y: clampedY,
      width: defaultWidth,
      height: defaultHeight,
      font_size: defaultFontSize,
    })
  }, [activePageNumber, onAddAnswerBox, pageWidth, getPageDims, scale])

  const handleAddCheckboxBoxClick = useCallback(() => {
    if (!onAddCheckboxBox || !containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const wrapper = containerRef.current.querySelector<HTMLElement>(
      `[data-pdf-page-wrapper="${activePageNumber}"]`,
    )
    if (!wrapper) return

    const wrapperRect = wrapper.getBoundingClientRect()
    const centerX = containerRect.left + containerRect.width / 2
    const centerY = containerRect.top + containerRect.height / 2

    const localX = centerX - wrapperRect.left
    const localY = centerY - wrapperRect.top

    const activePageDims = getPageDims(activePageNumber)
    const pixelsToPoints =
      activePageDims.width / (pageWidth || activePageDims.width)

    const defaultWidth = PDF_CUSTOM_CHECKBOX_DEFAULT_SIZE
    const defaultHeight = PDF_CUSTOM_CHECKBOX_DEFAULT_SIZE
    const defaultFontSize = PDF_CUSTOM_CHECKBOX_DEFAULT_SIZE - 4

    const xPoints = (localX / scale) * pixelsToPoints - defaultWidth / 2
    const yPoints = (localY / scale) * pixelsToPoints - defaultHeight / 2

    const clampedX = Math.max(
      0,
      Math.min(xPoints, activePageDims.width - defaultWidth),
    )
    const clampedY = Math.max(
      0,
      Math.min(yPoints, activePageDims.height - defaultHeight),
    )

    onAddCheckboxBox({
      page: activePageNumber,
      x: clampedX,
      y: clampedY,
      width: defaultWidth,
      height: defaultHeight,
      font_size: defaultFontSize,
    })
  }, [activePageNumber, onAddCheckboxBox, pageWidth, getPageDims, scale])

  const handleAddSignatureSelect = useCallback(
    (signature: SelectedSignature) => {
      if (!onAddSignature || !containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const wrapper = containerRef.current.querySelector<HTMLElement>(
        `[data-pdf-page-wrapper="${activePageNumber}"]`,
      )
      if (!wrapper) return

      const wrapperRect = wrapper.getBoundingClientRect()
      const centerX = containerRect.left + containerRect.width / 2
      const centerY = containerRect.top + containerRect.height / 2

      const localX = centerX - wrapperRect.left
      const localY = centerY - wrapperRect.top

      const activePageDims = getPageDims(activePageNumber)
      const pixelsToPoints =
        activePageDims.width / (pageWidth || activePageDims.width)

      const defaultWidth = 150
      // widthPx/heightPx are nullable — a signature with no known ratio falls
      // back to a square box rather than guessing.
      const ratio =
        signature.widthPx && signature.heightPx
          ? signature.heightPx / signature.widthPx
          : 1
      const defaultHeight = defaultWidth * ratio

      const xPoints = (localX / scale) * pixelsToPoints - defaultWidth / 2
      const yPoints = (localY / scale) * pixelsToPoints - defaultHeight / 2

      const clampedX = Math.max(
        0,
        Math.min(xPoints, activePageDims.width - defaultWidth),
      )
      const clampedY = Math.max(
        0,
        Math.min(yPoints, activePageDims.height - defaultHeight),
      )

      onAddSignature({
        page: activePageNumber,
        x: clampedX,
        y: clampedY,
        width: defaultWidth,
        height: defaultHeight,
        signatureId: signature.id,
      })
    },
    [activePageNumber, onAddSignature, pageWidth, getPageDims, scale],
  )

  const selectedBox = useMemo(
    () => highlightBoxesArray.find((b) => b.id === selectedBoxId),
    [highlightBoxesArray, selectedBoxId],
  )

  const displayedFontSize = useMemo(() => {
    const fontSize = selectedBox?.fontSize ?? PDF_ANSWER_BOX_DEFAULT_FONT_SIZE
    return (Math.round(fontSize * 100) / 100).toString()
  }, [selectedBox?.fontSize])

  const isAddBoxDisabled = !!isAddAnswerBoxPending || !!isAddCheckboxBoxPending

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {showControls && (
        <div className="flex items-center justify-between border-b bg-background px-2 py-2 shadow-xs">
          <div className="flex flex-1 flex-row items-center justify-start gap-3">
            {onAddAnswerBox ? (
              <PdfHeaderActionButton
                onClick={handleAddAnswerBoxClick}
                disabled={isAddBoxDisabled}
                title={translate("PDF_VIEWER/ADD_ANSWER_BOX")}
                icon={IconSquareLetterT}
                isLoading={isAddAnswerBoxPending}
              />
            ) : null}

            {onAddCheckboxBox ? (
              <PdfHeaderActionButton
                onClick={handleAddCheckboxBoxClick}
                disabled={isAddBoxDisabled}
                title={translate("PDF_VIEWER/ADD_CHECKBOX_BOX")}
                icon={IconSquareLetterX}
                isLoading={isAddCheckboxBoxPending}
              />
            ) : null}

            {onAddSignature ? (
              <PdfSignatureDropdown
                onSelect={handleAddSignatureSelect}
                disabled={isAddSignaturePending}
              />
            ) : null}
          </div>
          <div className="flex flex-1 flex-row items-center justify-end gap-3">
            {selectedBoxId && selectedBox?.kind === "signature" ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
                <button
                  onClick={() => onRemoveSignature?.(selectedBoxId)}
                  title={translate("PDF_VIEWER/REMOVE_SIGNATURE")}
                  aria-label={translate("PDF_VIEWER/REMOVE_SIGNATURE")}
                  className="flex items-center justify-center rounded p-0.5 text-destructive hover:bg-destructive/10 active:bg-destructive/20"
                >
                  <IconTrash size={16} />
                </button>
              </div>
            ) : (
              selectedBoxId && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
                  <button
                    onClick={handleDecreaseFontSize}
                    className="flex items-center justify-center rounded p-0.5 text-foreground hover:bg-secondary active:bg-secondary/80"
                  >
                    <span className="text-xs font-bold">A-</span>
                  </button>
                  <span className="min-w-[32px] text-center text-sm font-medium text-foreground">
                    {displayedFontSize}
                  </span>
                  <button
                    onClick={handleIncreaseFontSize}
                    className="flex items-center justify-center rounded p-0.5 text-foreground hover:bg-secondary active:bg-secondary/80"
                  >
                    <span className="text-xs font-bold">A+</span>
                  </button>
                </div>
              )
            )}

            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="rounded p-0.5 text-foreground hover:bg-secondary disabled:opacity-30"
              >
                <IconMinus size={16} />
              </button>
              <span className="min-w-[48px] text-center text-sm font-medium text-foreground">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                className="rounded p-0.5 text-foreground hover:bg-secondary disabled:opacity-30"
              >
                <IconPlus size={16} />
              </button>
            </div>

            {onClose && (
              <>
                <div className="mx-1 h-5 w-px bg-border" />
                <button
                  onClick={onClose}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  {translate("CLOSE")}
                  <IconX size={16} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* PDF Viewer - All Pages */}
      <div
        className="turnus-pdf-viewer bg-gray-100/50 flex-1 overflow-auto p-4"
        ref={containerRef}
        onScroll={handleScroll}
      >
        <div className="inline-block min-w-full">
          <div className="flex flex-col items-center">
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={onLoadingComponent}
              error={onErrorComponent}
              // Tailwind 3's drop-shadow-md; v4 redefined the scale.
              className="filter-[drop-shadow(0_4px_3px_rgb(0_0_0/0.07))_drop-shadow(0_2px_2px_rgb(0_0_0/0.06))]"
            >
              {numPages &&
                containerWidth &&
                Array.from(new Array(numPages), (el, index) => {
                  const pageNumber = index + 1
                  return (
                    <div
                      key={`page_${pageNumber}`}
                      style={{ position: "relative", width: "fit-content" }}
                      className="mb-6 last:mb-0"
                      data-pdf-page-wrapper={pageNumber}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={pageWidth}
                        scale={scale}
                        data-page-number={pageNumber}
                        className="bg-white"
                        renderAnnotationLayer={false}
                        renderTextLayer={true}
                        onRenderSuccess={(page) => {
                          // Page 1 seeds the document-wide fallback (used when USE_EXTEND is off).
                          if (pageNumber === 1 && page.originalWidth) {
                            setPdfPageWidthInPoints(page.originalWidth)
                          }
                          if (pageNumber === 1 && page.originalHeight) {
                            setPdfPageHeightInPoints(page.originalHeight)
                          }
                          // Record EVERY page's own dimensions so mixed-size PDFs scale each
                          // page's overlay boxes correctly. USE_EXTEND only: the map is never
                          // read when the flag is off, so skip the state update/rerender there.
                          if (
                            useExtend &&
                            page.originalWidth &&
                            page.originalHeight
                          ) {
                            setPageDimsByNumber((prev) => {
                              const existing = prev.get(pageNumber)
                              if (
                                existing &&
                                existing.width === page.originalWidth &&
                                existing.height === page.originalHeight
                              ) {
                                return prev
                              }
                              const next = new Map(prev)
                              next.set(pageNumber, {
                                width: page.originalWidth,
                                height: page.originalHeight,
                              })
                              return next
                            })
                          }
                        }}
                      />
                      {highlightBoxesArray
                        .filter((p) => p.pageNumber === pageNumber)
                        .map((box) => {
                          // Convert from PDF points to pixels using THIS page's own scale,
                          // then apply zoom. Per-page scale matters when the PDF mixes sizes.
                          const p2p = pointsToPixelsForPage(pageNumber)
                          const pixelX = box.x * p2p
                          const pixelY = box.y * p2p
                          const pixelW = box.width * p2p
                          const pixelH = box.height * p2p
                          return (
                            <PdfHighlightBox
                              bounds="parent"
                              id={`${box.id}`}
                              onBoxClick={() => onBoxClick?.(box.id)}
                              key={`${pageNumber}-${box.id}`}
                              fitText={fitHighlightBoxText}
                              kind={box.kind}
                              imageUrl={box.imageUrl}
                              lockAspectRatio={box.kind === "signature"}
                              layout={{
                                width: pixelW * scale,
                                height: pixelH * scale,
                                scale,
                                x: pixelX * scale,
                                y: pixelY * scale,
                                fontSize:
                                  (box.fontSize ||
                                    PDF_ANSWER_BOX_DEFAULT_FONT_SIZE) * p2p,
                              }}
                              onResize={(w, h, x, y) => {
                                handleResize(
                                  box.id,
                                  {
                                    width: Number(String(w).replace("px", "")),
                                    height: Number(String(h).replace("px", "")),
                                    x,
                                    y,
                                  },
                                  pageNumber,
                                )
                              }}
                              onDrag={(x, y) => {
                                handleDragStop(box.id, x, y, pageNumber)
                              }}
                              className={box.className}
                              isSelected={selectedBoxId === box.id}
                            >
                              {box.title}
                            </PdfHighlightBox>
                          )
                        })}
                    </div>
                  )
                })}
            </Document>
          </div>
        </div>
      </div>
    </div>
  )
}
